import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import "./FluxopayUnifiedConditions.css";

const brl = cents => (Number(cents || 0) / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const pct = value => `${Number(value || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%`;
const date = value => value ? new Date(value).toLocaleDateString("pt-BR") : "N\u00e3o informada";

export default function FluxopayUnifiedConditions({ platformConditions = [], anticipation = {} }) {
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const load = async () => {
    setLoading(true); setError("");
    try {
      const simulate = (payment_method, installments) => api.post("/api/fluxopay/asaas/simulate", { value_cents: 10000, payment_method, installments }).then(response => response.data);
      const [pix, cash, installment] = await Promise.all([simulate("pix", 1), simulate("credit_card", 1), simulate("credit_card", 2)]);
      setProvider({ pix, credit_card_1: cash, credit_card_2_12: installment });
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "N\u00e3o foi poss\u00edvel consultar as condi\u00e7\u00f5es do Asaas.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const rows = useMemo(() => [
    { key: "pix", label: "Pix", provider: provider?.pix },
    { key: "credit_card_1", label: "Cart\u00e3o \u00e0 vista", provider: provider?.credit_card_1 },
    { key: "credit_card_2_12", label: "Cart\u00e3o parcelado", detail: "Refer\u00eancia operacional em 2x", provider: provider?.credit_card_2_12 },
  ].map(row => ({ ...row, platform: platformConditions.find(item => item.key === row.key) })), [provider, platformConditions]);
  if (loading && !provider) {
    return (
      <p className="fxpay-note">
        {"Calculando condi\u00e7\u00f5es vigentes..."}
      </p>
    );
  }
  return <div className="fxuc">
    {error && <p className="fxpay-note fxuc-error">{error}</p>}
    {rows.map(row => <Condition key={row.key} {...row} />)}
    <div className="fxuc-anticipation">
      <div>
        <b>{"Antecipa\u00e7\u00e3o de receb\u00edveis"}</b>

        <span>
          {
            "Condi\u00e7\u00e3o opcional para antecipar vendas realizadas no cart\u00e3o"
          }
        </span>
      </div>

      {anticipation?.sample_size > 0 ? (
        <>
          <strong>
            {pct(anticipation.effective_percentage)}
          </strong>

          <small>
            {`Taxa m\u00e9dia efetiva \u00b7 tarifa m\u00e9dia de ${brl(
              Math.round(
                Number(anticipation.fee_cents || 0) /
                Number(anticipation.sample_size)
              )
            )} por antecipa\u00e7\u00e3o.`}
          </small>

          <small>
            {`Hist\u00f3rico: ${brl(
              anticipation.gross_cents
            )} antecipados, com ${brl(
              anticipation.fee_cents
            )} em tarifas.`}
          </small>

          <small>
            {
              "A taxa exata de uma nova antecipa\u00e7\u00e3o pode variar conforme o valor e a data prevista do receb\u00edvel."
            }
          </small>
        </>
      ) : (
        <>
          <strong>
            {"Taxa vari\u00e1vel"}
          </strong>

          <small>
            {
              "Ainda n\u00e3o existem antecipa\u00e7\u00f5es conclu\u00eddas para calcular uma taxa m\u00e9dia."
            }
          </small>

          <small>
            {
              "A taxa e a tarifa exatas s\u00e3o informadas pelo Asaas na simula\u00e7\u00e3o disponível em A receber, antes da confirma\u00e7\u00e3o."
            }
          </small>
        </>
      )}
    </div>
    <p className="fxpay-note">
      {"Estimativas calculadas sobre uma venda de R$ 100,00. Os valores definitivos s\u00e3o recalculados no pagamento."}
    </p>
    <button className="fxuc-refresh" type="button" onClick={load} disabled={loading}>{loading ? "Atualizando..." : "Atualizar condi\u00e7\u00f5es"}</button>
  </div>;
}

function Condition({ label, detail, provider, platform }) {
  const asaas = Number(provider?.provider_fee_cents || 0);
  const fluxopay = Number(provider?.platform_fee_cents ?? platform?.platform_fee_fixed_cents ?? 0);
  const condition = provider?.provider_condition;
  return <article className="fxuc-condition">
    <header><div><b>{label}</b>{detail && <span>{detail}</span>}</div></header>
    <div className="fxuc-composition">
      <Box label="Custo Asaas" amount={provider ? brl(asaas) : "\u2014"} detail={provider ? composition(provider.provider_fee_percentage, provider.provider_operation_fee_cents, asaas) : "Indispon\u00edvel"}/>
      <Box label="Taxa Fluxopay" amount={platform ? brl(fluxopay) : "\u2014"} detail={platform ? composition(platform.platform_fee_percentage, platform.platform_fee_fixed_cents, fluxopay, "Sem taxa") : "Regra n\u00e3o configurada"}/>
      <Box label="Desconto total estimado" amount={provider && platform ? brl(asaas + fluxopay) : "\u2014"} detail="Sobre R$ 100,00" total/>
    </div>
    {condition && (
      <div className="fxuc-validity">
        <span>
          <b>Vigente desde</b>
          {date(condition.effective_from)}
        </span>

        {condition.promotion_ends_at && (
          <span>
            <b>{"Fim estimado da promo\u00e7\u00e3o"}</b>
            {date(condition.promotion_ends_at)}
          </span>
        )}

        {condition.promotion_ends_at && (
          <span>
            <b>{"Condi\u00e7\u00e3o posterior"}</b>
            {composition(
              condition.standard_percentage,
              condition.standard_fixed_cents,
              0,
              "Sem taxa"
            )}
          </span>
        )}

        <span>
          <b>Fonte</b>
          {"API Asaas \u00b7 "}
          {date(condition.observed_at)}
        </span>
      </div>
    )}

    {platform?.provider_effective_percentage != null && (
      <small className="fxuc-history">
        {`Custo efetivo conciliado: ${pct(
          platform.provider_effective_percentage
        )} em ${platform.sample_size} transa\u00e7\u00e3o(\u00f5es).`}
      </small>
    )}
  </article>;
}
function composition(percentage, fixedCents, fallbackCents, empty = null) {
  const parts = [Number(percentage || 0) > 0 ? pct(percentage) : null, Number(fixedCents || 0) > 0 ? `${brl(fixedCents)} fixos` : null].filter(Boolean);
  return parts.join(" + ") || empty || `${brl(fallbackCents)} fixos`;
}
function Box({ label, amount, detail, total }) { return <div className={total ? "is-total" : ""}><small>{label}</small><b>{amount}</b><span>{detail}</span></div>; }
